import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, type ActionIconProps, Tooltip, type TooltipProps} from '@mantine/core';
import React, {useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import MantineDropdownMenu, {MantineDropdownMenuProps} from './MantineDropdownMenu';

export type {MantineDropdownMenuOption as MantineOverflowMenuOption} from './MantineDropdownMenu';
export type {MantineDropdownMenuSection as MantineOverflowMenuSection} from './MantineDropdownMenu';

export interface MantineOverflowMenuProps extends Omit<MantineDropdownMenuProps, 'trigger' | 'onOpenChange'> {
  iconSize?: ActionIconProps['size'];
  /** Whether to use a vertical ellipsis icon instead of horizontal. */
  isIconVertical?: boolean;
  /** Tooltip text shown on the trigger button. Defaults to 'More options'. */
  triggerTooltip?: string;
  /** Position of the tooltip relative to the trigger. Defaults to Mantine's default. */
  tooltipPosition?: TooltipProps['position'];
}

/**
 * A menu for hiding extra buttons that don't fit in the main bar. Has an ellipsis "..." icon as the trigger.
 */
export default function MantineOverflowMenu({
  iconSize = 'md',
  isIconVertical = false,
  triggerTooltip = 'More options',
  tooltipPosition,
  ...props
}: MantineOverflowMenuProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const trigger = (
    <Tooltip label={triggerTooltip} position={tooltipPosition} disabled={isMenuOpen}>
      <ActionIcon
        size={iconSize}
        aria-label={triggerTooltip}
        {...getTestProps({component: 'MantineOverflowMenu', element: 'trigger'})}
      >
        <BenchlingIcon icon={isIconVertical ? 'ellipsis-vertical' : 'ellipsis'} />
      </ActionIcon>
    </Tooltip>
  );

  return <MantineDropdownMenu {...props} onOpenChange={setIsMenuOpen} trigger={trigger} />;
}
